import React, { useState, useRef, useEffect } from 'react';
import { Plus, User, Music, Play, Pause, Upload, X, Send, CheckCircle, LogOut, Loader2, BookMarked, KeyRound, Users, ChevronDown, ChevronLeft, LogIn, AlertTriangle, Trash2, Pencil, Settings, Info } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  onSnapshot,
  query,
  orderBy,
  deleteDoc,
  updateDoc,
  getDoc,
  setDoc
} from 'firebase/firestore';


// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyC1ObXwuf9OLiGlYCQzSt2BmaMXb4PokAg",
  authDomain: "soundfile-8c201.firebaseapp.com",
  projectId: "soundfile-8c201",
  storageBucket: "soundfile-8c201.appspot.com",
  messagingSenderId: "574716616720",
  appId: "1:574716616720:web:586ee81b585ccbf15baa95",
  measurementId: "G-VS5X640TM3"
};

// --- Initialize Firebase ---
let app;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  console.log("Firebase app already initialized.");
}

const auth = getAuth(app);
const db = getFirestore(app);


// --- Reusable Components ---

const Notification = ({ message, show, isError = false }) => {
  if (!show) return null;
  return (
    <div className={`fixed top-20 left-1/2 -translate-x-1/2 backdrop-blur-md text-white px-6 py-3 rounded-lg shadow-2xl z-[100] flex items-center animate-fade-in-down ${isError ? 'bg-red-600/90' : 'bg-green-600/90'}`}>
      {isError ? <AlertTriangle className="w-6 h-6 mr-3" /> : <CheckCircle className="w-6 h-6 mr-3" />}
      <span>{message}</span>
    </div>
  );
};

const LoadingSpinner = ({ theme = 'dark' }) => (
    <div className={`flex items-center justify-center h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-200'}`}>
        <Loader2 className={`w-16 h-16 animate-spin ${theme === 'dark' ? 'text-cyan-400' : 'text-gray-700'}`} />
    </div>
);

const GuestWarningBanner = ({ onLoginClick }) => (
    <div className="bg-yellow-400/80 backdrop-blur-md border-b border-yellow-600 text-yellow-900 px-6 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-center space-x-2 text-sm font-semibold">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>You are not logged in. Your work will not be saved.</span>
            <button onClick={onLoginClick} className="underline hover:text-black">Login or Sign Up to save.</button>
        </div>
    </div>
);

const ConfirmationModal = ({ title, message, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
        <div className="bg-gray-200/90 backdrop-blur-2xl rounded-2xl border border-black/10 p-8 w-full max-w-md shadow-2xl">
            <h3 className="text-xl font-bold text-gray-800 mb-4">{title}</h3>
            <p className="text-gray-700 mb-6">{message}</p>
            <div className="flex justify-end space-x-4">
                <button onClick={onCancel} className="px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold">Cancel</button>
                <button onClick={onConfirm} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold">Confirm Delete</button>
            </div>
        </div>
    </div>
);

// --- Authentication Modal Component ---
const AuthModal = ({ setNotification, onClose }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [authMode, setAuthMode] = useState('login'); // 'login', 'signup', 'forgot'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleAuth = async (e) => {
        e.preventDefault(); setLoading(true); setError('');
        try {
            if (authMode === 'login') {
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                await createUserWithEmailAndPassword(auth, email, password);
                setNotification({ show: true, message: "Account created! Welcome!" });
            }
            onClose();
        } catch (err) { setError(err.message.replace('Firebase: ', '')); }
        setLoading(false);
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        if (!email) { setError("Please enter your email address."); return; }
        setLoading(true); setError('');
        try {
            await sendPasswordResetEmail(auth, email);
            setNotification({ show: true, message: "Password reset link sent!" });
            setAuthMode('login');
        } catch (err) { setError(err.message.replace('Firebase: ', '')); }
        setLoading(false);
    };
    
    const getTitle = () => {
        if (authMode === 'login') return 'Log In';
        if (authMode === 'signup') return 'Create Account';
        if (authMode === 'forgot') return 'Reset Password';
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-white/50 shadow-2xl p-8 w-full max-w-md relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-black"><X/></button>
                <div className="flex justify-center mb-6">
                    <img src="https://i.postimg.cc/PxdZK6zf/MULTI-2-Photoroom.png" alt="SoundFile Logo" className="h-32" />
                </div>
                <h2 className="text-3xl font-bold text-gray-800 text-center mb-6">{getTitle()}</h2>
                {authMode === 'forgot' ? (
                    <form onSubmit={handlePasswordReset} className="space-y-6">
                        <p className="text-center text-gray-700 text-sm">Enter your email and we'll send a reset link.</p>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Address" required className="w-full bg-white/50 text-gray-900 placeholder-gray-600 rounded-lg px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-700 outline-none transition" />
                        {error && <p className="text-red-600 text-sm text-center font-medium">{error}</p>}
                        <button type="submit" disabled={loading} className="w-full py-3 rounded-lg font-semibold transition-all shadow-lg bg-gray-800 hover:bg-gray-900 text-white disabled:opacity-50 flex items-center justify-center">{loading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />} Send Reset Link</button>
                    </form>
                ) : (
                    <form onSubmit={handleAuth} className="space-y-6">
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Address" required className="w-full bg-white/50 text-gray-900 placeholder-gray-600 rounded-lg px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-700 outline-none transition" />
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required className="w-full bg-white/50 text-gray-900 placeholder-gray-600 rounded-lg px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-700 outline-none transition" />
                        {authMode === 'login' && (<div className="text-right"><button type="button" onClick={() => setAuthMode('forgot')} className="text-xs font-medium text-gray-600 hover:text-black">Forgot Password?</button></div>)}
                        {error && <p className="text-red-600 text-sm text-center font-medium">{error}</p>}
                        <button type="submit" disabled={loading} className="w-full py-3 rounded-lg font-semibold transition-all shadow-lg bg-gray-800 hover:bg-gray-900 text-white disabled:opacity-50 flex items-center justify-center">{loading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />} {authMode === 'login' ? 'Log In' : 'Sign Up'}</button>
                    </form>
                )}
                <div className="text-center mt-6">
                    {authMode === 'forgot' ? (<button onClick={() => setAuthMode('login')} className="text-gray-600 hover:text-black text-sm font-medium">← Back to Login</button>) : (<button onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')} className="text-gray-600 hover:text-black text-sm font-medium">{authMode === 'login' ? "Don't have an account? Sign Up" : "Already have an account? Log In"}</button>)}
                </div>
            </div>
        </div>
    );
};

// --- Profile Modal Component ---
const ProfileModal = ({ user, setNotification, onClose }) => {
    const [displayName, setDisplayName] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState({ name: '', password: '' });

    useEffect(() => {
        setDisplayName(user.displayName || '');
    }, [user]);

    const handleNameUpdate = async (e) => {
        e.preventDefault(); setLoading(true); setError({ ...error, name: '' });
        try {
            const userRef = doc(db, "users", user.uid);
            await setDoc(userRef, { displayName: displayName }, { merge: true });
            setNotification({ show: true, message: "Profile name updated!" });
        } catch (err) { setError({ ...error, name: "Failed to update name." }); }
        setLoading(false);
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (!currentPassword || !newPassword) { setError({ ...error, password: "All password fields are required." }); return; }
        setLoading(true); setError({ ...error, password: '' });
        try {
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(auth.currentUser, credential);
            await updatePassword(auth.currentUser, newPassword);
            setNotification({ show: true, message: "Password updated successfully!" });
            setCurrentPassword(''); setNewPassword('');
        } catch (err) { setError({ ...error, password: "Failed to update password. Check your current password." }); console.error(err); }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
            <div className="bg-gray-200/90 backdrop-blur-2xl rounded-2xl border border-black/10 p-8 w-full max-w-md relative shadow-2xl">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-black"><X /></button>
                <h2 className="text-3xl font-bold text-gray-800 text-center mb-8">Profile Settings</h2>
                <form onSubmit={handleNameUpdate} className="space-y-4 mb-8">
                    <h3 className="font-semibold text-lg">Update Your Name</h3>
                    <div>
                        <label className="text-sm font-medium text-gray-600">Display Name</label>
                        <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your Name" className="w-full mt-1 bg-white/80 text-gray-900 placeholder-gray-500 rounded-lg px-4 py-2 border border-gray-300"/>
                    </div>
                    {error.name && <p className="text-red-600 text-sm text-center font-medium">{error.name}</p>}
                    <button type="submit" disabled={loading} className="w-full py-2 rounded-lg font-semibold transition-all bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex items-center justify-center">{loading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />} Update Name</button>
                </form>
                <div className="border-t border-black/20 my-8"></div>
                <form onSubmit={handlePasswordUpdate} className="space-y-4">
                    <h3 className="font-semibold text-lg">Change Password</h3>
                     <div>
                        <label className="text-sm font-medium text-gray-600">Current Password</label>
                        <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="••••••••" className="w-full mt-1 bg-white/80 text-gray-900 placeholder-gray-500 rounded-lg px-4 py-2 border border-gray-300"/>
                    </div>
                     <div>
                        <label className="text-sm font-medium text-gray-600">New Password</label>
                        <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" className="w-full mt-1 bg-white/80 text-gray-900 placeholder-gray-500 rounded-lg px-4 py-2 border border-gray-300"/>
                    </div>
                    {error.password && <p className="text-red-600 text-sm text-center font-medium">{error.password}</p>}
                    <button type="submit" disabled={loading} className="w-full py-2 rounded-lg font-semibold transition-all bg-gray-800 hover:bg-black text-white disabled:opacity-50 flex items-center justify-center">{loading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />} Change Password</button>
                </form>
            </div>
        </div>
    );
};

const ContactFormModal = ({ isOpen, onClose, setNotification, user, contactToEdit }) => {
    const [contactData, setContactData] = useState({ name: '', email: '', phone: '', type: 'Artist' });
    
    useEffect(() => {
        if(contactToEdit) {
            setContactData(contactToEdit);
        } else {
            setContactData({ name: '', email: '', phone: '', type: 'Artist' });
        }
    }, [contactToEdit, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (contactData.name.trim()) {
          const finalData = {name: contactData.name.trim(), email: contactData.email, phone: contactData.phone, type: contactData.type};
          if(user) {
            if(contactToEdit?.id) {
                const contactRef = doc(db, "users", user.uid, "contacts", contactToEdit.id);
                await updateDoc(contactRef, finalData);
                setNotification({show: true, message: `Contact "${finalData.name}" updated.`});
            } else {
                await addDoc(collection(db, "users", user.uid, "contacts"), finalData);
                setNotification({show: true, message: `Contact "${finalData.name}" added.`});
            }
          }
          onClose();
        }
    }

    return (
         <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"><div className="bg-gray-200/90 backdrop-blur-2xl rounded-2xl border border-black/10 p-8 w-full max-w-md"><div className="flex items-center justify-between mb-6"><h3 className="text-xl font-bold text-gray-800">{contactToEdit ? 'Edit Contact' : 'Add New Contact'}</h3><button onClick={onClose} className="text-gray-600 hover:text-black"><X /></button></div><form onSubmit={handleSubmit} className="space-y-4"><input type="text" placeholder="Name" value={contactData.name} onChange={e => setContactData(p => ({ ...p, name: e.target.value }))} className="w-full bg-white/80 text-gray-900 placeholder-gray-500 rounded-lg px-4 py-3 border border-gray-300" /><input type="email" placeholder="Email" value={contactData.email} onChange={e => setContactData(p => ({ ...p, email: e.target.value }))} className="w-full bg-white/80 text-gray-900 placeholder-gray-500 rounded-lg px-4 py-3 border border-gray-300" /><input type="tel" placeholder="Phone" value={contactData.phone} onChange={e => setContactData(p => ({ ...p, phone: e.target.value }))} className="w-full bg-white/80 text-gray-900 placeholder-gray-500 rounded-lg px-4 py-3 border border-gray-300" /><select value={contactData.type} onChange={e => setContactData(p => ({ ...p, type: e.target.value }))} className="w-full bg-white/80 text-gray-900 rounded-lg px-4 py-3 border border-gray-300"><option>Artist</option><option>A&R</option><option>Label</option><option>Producer</option><option>Engineer</option></select><button type="submit" disabled={!contactData.name.trim()} className={`w-full py-3 rounded-lg font-semibold transition-all ${contactData.name.trim() ? 'bg-gray-800 hover:bg-black text-white' : 'bg-gray-400 text-gray-200 cursor-not-allowed'}`}>{contactToEdit ? 'Update Contact' : 'Add Contact'}</button></form></div></div>
    );
};


// --- Main Application Component ---

const MusicCRM = ({ user, setNotification }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [selectedPackForHistory, setSelectedPackForHistory] = useState(null);
  const [showAllBeats, setShowAllBeats] = useState(false);
  const [showAllContacts, setShowAllContacts] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  const [contacts, setContacts] = useState([]);
  const [beats, setBeats] = useState([]);
  const [sentPacks, setSentPacks] = useState([]);
  
  const [currentSend, setCurrentSend] = useState({ id: null, packName: '', selectedContact: '', selectedBeats: [] });
  
  const [dragOver, setDragOver] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [localBeatUrls, setLocalBeatUrls] = useState(new Map());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [packToDelete, setPackToDelete] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [dropboxFiles, setDropboxFiles] = useState([]);
const [dropboxLibraryToken, setDropboxLibraryToken] = useState(null);
const [libraryFiles, setLibraryFiles] = useState([]);
const [currentLibraryPath, setCurrentLibraryPath] = useState('');
const [loadingLibrary, setLoadingLibrary] = useState(false);
const [uploadingFiles, setUploadingFiles] = useState(false);
const [selectedLibraryFiles, setSelectedLibraryFiles] = useState([]);
const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const libraryUploadRef = useRef(null);
  // --- useEffects ---
  useEffect(() => {
    if (user) {
        const sentPacksQuery = query(collection(db, "users", user.uid, "sentPacks"), orderBy("date", "desc"));
        const contactsQuery = query(collection(db, "users", user.uid, "contacts"), orderBy("name"));
        const beatsQuery = query(collection(db, "users", user.uid, "beats"), orderBy("title"));
        
        const unsubContacts = onSnapshot(contactsQuery, (s) => setContacts(s.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubBeats = onSnapshot(beatsQuery, (s) => setBeats(s.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubPacks = onSnapshot(sentPacksQuery, (s) => setSentPacks(s.docs.map(d => ({ id: d.id, ...d.data() }))));
        
        return () => { unsubContacts(); unsubBeats(); unsubPacks(); };
 
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => { if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsDropdownOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);
  
  const openAddContactModal = () => {
    setContactToEdit(null);
    setShowContactForm(true);
  };

  const openEditContactModal = (contact) => {
    setContactToEdit(contact);
    setShowContactForm(true);
  };
  
  const handleDeleteContact = async (contactId) => {
    if(user) {
        const contactRef = doc(db, "users", user.uid, "contacts", contactId);
        await deleteDoc(contactRef);
    }
    setNotification({show: true, message: `Contact deleted successfully.`});
    setContactToDelete(null);
  };
// Load Dropbox Chooser script
useEffect(() => {
  const existingScript = document.getElementById('dropboxjs');
  if (existingScript) return; // Already loaded
  
  const script = document.createElement('script');
  script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
  script.id = 'dropboxjs';
  script.setAttribute('data-app-key', 'n7fauiq8m7wrm0r');
  document.head.appendChild(script);
  
  script.onload = () => {
    console.log('Dropbox Chooser loaded:', window.Dropbox);
  };
}, []);
// Handle Dropbox Library OAuth callback
useEffect(() => {
  const hash = window.location.hash;
  if (hash.includes('access_token') && window.location.pathname.includes('/dropbox/library/callback')) {
    const token = hash.match(/access_token=([^&]*)/)[1];
    setDropboxLibraryToken(token);
    localStorage.setItem('dropboxLibraryToken', token);
    window.history.replaceState(null, '', '/');
    setNotification({ show: true, message: 'Connected to Dropbox Library!' });
    setActiveTab('library');
    loadLibraryFolder('');
  }
  
  // Load saved library token on mount
  const savedLibraryToken = localStorage.getItem('dropboxLibraryToken');
  if (savedLibraryToken) {
    setDropboxLibraryToken(savedLibraryToken);
  }
}, []);

// Load library when token is available and on Library tab
useEffect(() => {
  if (dropboxLibraryToken && activeTab === 'library' && libraryFiles.length === 0) {
    loadLibraryFolder(currentLibraryPath);
  }
}, [dropboxLibraryToken, activeTab]);
  // --- Core Logic ---
  const handleLogOrUpdatePack = async () => {
    if (currentSend.selectedContact && currentSend.selectedBeats.length > 0) {
      const packData = { contactId: currentSend.selectedContact, beatIds: currentSend.selectedBeats, date: new Date().toISOString(), packName: currentSend.packName || '' };
      const recipientName = contacts.find(c => c.id === currentSend.selectedContact)?.name || 'the recipient';
      
      if (user) {
        if (currentSend.id) {
          const packRef = doc(db, "users", user.uid, "sentPacks", currentSend.id);
          await updateDoc(packRef, packData);
          setNotification({show: true, message: `Pack for ${recipientName} updated!`});
        } else {
          await addDoc(collection(db, "users", user.uid, "sentPacks"), packData);
          setNotification({show: true, message: `Successfully logged pack for ${recipientName}!`});
        }
      } else {
        if (currentSend.id) setSentPacks(prev => prev.map(p => p.id === currentSend.id ? {...packData, id: currentSend.id} : p));
        else setSentPacks(prev => [{...packData, id: Date.now().toString()}, ...prev]);
        setNotification({show: true, message: `Successfully logged pack for ${recipientName}!`});
      }
      
      setCurrentSend({ id: null, packName: '', selectedContact: '', selectedBeats: [] });
    }
  };
  
  const handleEditPack = (pack) => {
    setActiveTab('home');
    setCurrentSend({ id: pack.id, packName: pack.packName || '', selectedContact: pack.contactId, selectedBeats: pack.beatIds });
  };

  const handleDeletePack = async (packId) => {
    if(user) {
        const packRef = doc(db, "users", user.uid, "sentPacks", packId);
        await deleteDoc(packRef);
    } else {
        setSentPacks(prev => prev.filter(p => p.id !== packId));
    }
    setNotification({show: true, message: `Pack deleted successfully.`});
    setPackToDelete(null);
  };
  
  const handleContactClick = (contactId) => {
    setActiveTab('home');
    setCurrentSend({ id: null, packName: '', selectedContact: contactId, selectedBeats: [] });
  };
  // --- Dropbox Functions ---
// --- Dropbox Functions (using Chooser API) ---
const connectDropbox = () => {
  console.log('Dropbox object:', window.Dropbox);
  
  if (!window.Dropbox || !window.Dropbox.choose) {
    console.error('Dropbox not loaded yet');
    setNotification({ show: true, message: 'Dropbox is still loading... Please wait a moment and try again.', isError: true });
    return;
  }
  
  console.log('Opening Dropbox chooser...');
  
  const options = {
    success: function(files) {
      console.log('Files selected:', files);
      setDropboxFiles(files);
      addDropboxFilesToPack(files);
    },
    cancel: function() {
      console.log('User cancelled');
    },
    linkType: "direct",
    multiselect: true,
    extensions: ['.mp3', '.wav', '.m4a', '.flac'],
    folderselect: false,
  };
  
  try {
    window.Dropbox.choose(options);
  } catch (error) {
    console.error('Error opening Dropbox:', error);
    setNotification({ show: true, message: 'Error opening Dropbox: ' + error.message, isError: true });
  }
};

const addDropboxFilesToPack = (files) => {
  files.forEach(async (file) => {
    const beatTitle = file.name.replace(/\.[^/.]+$/, "");
    let existingBeat = beats.find(b => b.title.toLowerCase() === beatTitle.toLowerCase());
    let beatId = existingBeat?.id;
    
    if (!existingBeat) {
      const newBeatData = { 
        title: beatTitle, 
        date: new Date().toISOString(), 
        dropboxLink: file.link 
      };
      if(user) {
        const docRef = await addDoc(collection(db, "users", user.uid, "beats"), newBeatData);
        beatId = docRef.id;
      } else {
        beatId = Date.now().toString() + Math.random();
        setBeats(prev => [...prev, {...newBeatData, id: beatId}]);
      }
    }
    
    // Store the Dropbox link for playback
    setLocalBeatUrls(prev => new Map(prev).set(beatId, file.link));
    
    setCurrentSend(prev => ({ 
      ...prev, 
      selectedBeats: prev.selectedBeats.includes(beatId) ? prev.selectedBeats : [...prev.selectedBeats, beatId]
    }));
  });
  
  setNotification({ show: true, message: 'Files added to current pack!' });
};
// --- Dropbox Library Functions ---
const connectDropboxLibrary = () => {
  const clientId = process.env.REACT_APP_DROPBOX_LIBRARY_APP_KEY;
  const redirectUri = window.location.origin + '/dropbox/library/callback';
  const authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${clientId}&response_type=token&redirect_uri=${encodeURIComponent(redirectUri)}`;
  window.location.href = authUrl;
};

const loadLibraryFolder = async (path = '') => {
  if (!dropboxLibraryToken) return;
  setLoadingLibrary(true);
  try {
    const response = await fetch('https://api.dropboxapi.com/2/files/list_folder', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${dropboxLibraryToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ path: path })
    });
    const data = await response.json();
    setLibraryFiles(data.entries || []);
    setCurrentLibraryPath(path);
  } catch (error) {
    console.error('Error loading library:', error);
    setNotification({ show: true, message: 'Error loading library', isError: true });
  }
  setLoadingLibrary(false);
};

const uploadToLibrary = async (files) => {
  if (!dropboxLibraryToken) return;
  setUploadingFiles(true);
  
  for (const file of files) {
    try {
      const response = await fetch('https://content.dropboxapi.com/2/files/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${dropboxLibraryToken}`,
          'Dropbox-API-Arg': JSON.stringify({
            path: `${currentLibraryPath}/${file.name}`,
            mode: 'add',
            autorename: true
          }),
          'Content-Type': 'application/octet-stream',
        },
        body: file
      });
      
      if (response.ok) {
        console.log(`Uploaded: ${file.name}`);
      }
    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
    }
  }
  
  setUploadingFiles(false);
  setNotification({ show: true, message: `${files.length} file(s) uploaded!` });
  loadLibraryFolder(currentLibraryPath);
};

const disconnectLibrary = () => {
  setDropboxLibraryToken(null);
  setLibraryFiles([]);
  setCurrentLibraryPath('');
  localStorage.removeItem('dropboxLibraryToken');
  setNotification({ show: true, message: 'Disconnected from Library' });
};
const deleteLibraryFile = async (filePath) => {
  if (!dropboxLibraryToken) return;
  try {
    await fetch('https://api.dropboxapi.com/2/files/delete_v2', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${dropboxLibraryToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ path: filePath })
    });
    setNotification({ show: true, message: 'File deleted' });
    loadLibraryFolder(currentLibraryPath);
  } catch (error) {
    console.error('Error deleting file:', error);
    setNotification({ show: true, message: 'Error deleting file', isError: true });
  }
};

const createLibraryFolder = async (folderName) => {
  if (!dropboxLibraryToken || !folderName) return;
  try {
    await fetch('https://api.dropboxapi.com/2/files/create_folder_v2', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${dropboxLibraryToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ path: `${currentLibraryPath}/${folderName}` })
    });
    setNotification({ show: true, message: 'Folder created' });
    loadLibraryFolder(currentLibraryPath);
  } catch (error) {
    console.error('Error creating folder:', error);
    setNotification({ show: true, message: 'Error creating folder', isError: true });
  }
};

const addLibraryFileToPack = async (file) => {
  try {
    const response = await fetch('https://api.dropboxapi.com/2/files/get_temporary_link', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${dropboxLibraryToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ path: file.path_display })
    });
    const data = await response.json();
    const fileData = { name: file.name, link: data.link };
    addDropboxFilesToPack([fileData]);
    setNotification({ show: true, message: `Added ${file.name} to current pack!` });
  } catch (error) {
    console.error('Error adding file:', error);
    setNotification({ show: true, message: 'Error adding file', isError: true });
  }
};
  const processFiles = async (fileList) => {
    const files = Array.from(fileList).filter(f => f.type.startsWith('audio/'));
    if (files.length === 0) return;
    
    let updatedSelectedBeats = [...currentSend.selectedBeats];
    for (const file of files) {
      const beatTitle = file.name.replace(/\.[^/.]+$/, "");
      let existingBeat = beats.find(b => b.title.toLowerCase() === beatTitle.toLowerCase());
      let beatId = existingBeat?.id;
      if (!existingBeat) {
        const newBeatData = { title: beatTitle, date: new Date().toISOString() };
        if(user) {
          const docRef = await addDoc(collection(db, "users", user.uid, "beats"), newBeatData);
          beatId = docRef.id;
        } else {
          beatId = Date.now().toString() + Math.random();
          setBeats(prev => [...prev, {...newBeatData, id: beatId}]);
        }
      }
      const url = URL.createObjectURL(file);
      setLocalBeatUrls(prev => new Map(prev).set(beatId, url));
      if (!updatedSelectedBeats.includes(beatId)) updatedSelectedBeats.push(beatId);
    }
    setCurrentSend(prev => ({ ...prev, selectedBeats: updatedSelectedBeats }));
  };

  const handleDrop = async (e) => {
    e.preventDefault(); 
    setDragOver(false);
    if (e.dataTransfer.files) {
        await processFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = async (e) => {
    if (e.target.files) {
        await processFiles(e.target.files);
    }
  };
  
  const playAudio = (beatId, url) => {
    const playbackUrl = url || localBeatUrls.get(beatId);
    if (!playbackUrl) { setNotification({show: true, message: "Playback only available for session uploads."}); return; }
    if (currentlyPlaying === beatId) { audioRef.current.pause(); setCurrentlyPlaying(null); } 
    else if (audioRef.current) { audioRef.current.src = playbackUrl; audioRef.current.play(); setCurrentlyPlaying(beatId); }
  };
  
  const removeBeatFromSend = (beatId) => setCurrentSend(prev => ({...prev, selectedBeats: prev.selectedBeats.filter(id => id !== beatId) }));
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const selectedContactInfo = contacts.find(c => c.id === currentSend.selectedContact);
  const selectedBeatsData = beats.filter(b => currentSend.selectedBeats.includes(b.id)).map(beat => ({...beat, url: localBeatUrls.get(beat.id)}));
  const filteredContacts = contacts.filter(contact => contact.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const historyContact = selectedPackForHistory ? contacts.find(c => c.id === selectedPackForHistory.contactId) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F6EE] via-[#BCC6CC] to-[#F5F5F5] text-gray-800 font-sans relative">
      {showAuthModal && <AuthModal setNotification={setNotification} onClose={() => setShowAuthModal(false)} />}
      {showProfileModal && user && <ProfileModal user={user} setNotification={setNotification} onClose={() => setShowProfileModal(false)} />}
      {packToDelete && <ConfirmationModal title="Delete Pack" message="Are you sure you want to permanently delete this logged pack? This action cannot be undone." onConfirm={() => handleDeletePack(packToDelete)} onCancel={() => setPackToDelete(null)} />}
      {contactToDelete && <ConfirmationModal title="Delete Contact" message={`Are you sure you want to delete ${contactToDelete.name}? This will also remove them from any logged packs.`} onConfirm={() => handleDeleteContact(contactToDelete.id)} onCancel={() => setContactToDelete(null)} />}
      <ContactFormModal isOpen={showContactForm} onClose={() => setShowContactForm(false)} setNotification={setNotification} user={user} contactToEdit={contactToEdit} />
      
      <header className="bg-white/40 backdrop-blur-xl border-b border-black/10 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-2 relative flex items-center">
            <div className="flex-1"></div>
            <div className="flex-1 flex justify-center">
                <img src="https://i.postimg.cc/PxdZK6zf/MULTI-2-Photoroom.png" alt="SoundFile Logo" className="h-32" />
            </div>
            <div className="flex-1 flex justify-end">
                <div className="flex items-center space-x-4 text-sm font-medium">
                    {user ? (
                        <>
                            <button onClick={() => setShowProfileModal(true)} className="flex items-center text-gray-700 hover:text-black transition-colors"><Settings className="w-4 h-4 mr-2" />Profile</button>
                            <button onClick={() => signOut(auth)} className="flex items-center text-red-600 hover:text-red-800 transition-colors"><LogOut className="w-4 h-4 mr-2" />Logout</button>
                        </>
                    ) : (
                        <button onClick={() => setShowAuthModal(true)} className="flex items-center bg-gray-800 text-white hover:bg-black px-4 py-2 rounded-lg transition-colors shadow-md"><LogIn className="w-4 h-4 mr-2" />Login / Sign Up</button>
                    )}
                </div>
            </div>
        </div>
      </header>
      
      {!user && <GuestWarningBanner onLoginClick={() => setShowAuthModal(true)} />}
      
      <div className="text-center py-8 px-6"><h2 className="text-xl font-medium text-gray-700 tracking-wide">Organize and track which files you sent to who, and when!</h2></div>

      <nav className="bg-white/20 backdrop-blur-xl border-y border-black/10 relative z-30 shadow-md">
        <div className="max-w-7xl mx-auto px-6 flex justify-center space-x-1">
{[{id:'home', label:'Home'}, {id:'contacts', label:'Contacts'}, {id:'sound-sent', label:'Packs I Sent'}, {id:'library', label:'Library'}, {id:'about', label:'About'}].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-6 py-4 font-semibold transition-all duration-300 relative ${activeTab === tab.id ? 'text-gray-900' : 'text-gray-600 hover:text-black'}`}>
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-gray-800 rounded-full"></div>}
            </button>
          ))}
        </div>
      </nav>
      
      <main className="max-w-6xl mx-auto px-6 py-12 relative z-10">
        {activeTab === 'home' && (<div className="bg-black/5 backdrop-blur-2xl rounded-3xl border border-black/10 shadow-xl p-8 space-y-8"><div className="flex items-center justify-between"><div className="flex items-center"><div className="bg-gray-800 rounded-full p-3 mr-4"><BookMarked className="w-6 h-6 text-white" /></div><h2 className="text-3xl font-bold text-gray-800">File Away New Pack Sent</h2></div><button onClick={openAddContactModal} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-lg"><Plus className="w-4 h-4 mr-2" />Add Contact</button></div><div><label className="block text-gray-800 text-lg font-medium mb-4">1. Pack Name (Optional)</label><input type="text" value={currentSend.packName} onChange={(e) => setCurrentSend(p => ({...p, packName: e.target.value}))} placeholder="e.g. Beats for Album" className="w-full p-4 rounded-xl border-2 border-black/20 bg-black/5 focus:border-black/40 outline-none transition" /></div><div><label className="block text-gray-800 text-lg font-medium mb-4">2. Select Recipient</label><div className="relative" ref={dropdownRef}><button type="button" onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full p-4 rounded-xl border-2 border-black/20 bg-black/5 text-left flex items-center justify-between hover:border-black/40 transition">{selectedContactInfo ? (<div className="flex items-center"><div className="bg-gray-700 rounded-full p-2 mr-3"><User className="w-4 h-4 text-white" /></div><div><h3 className="text-gray-800 font-semibold">{selectedContactInfo.name}</h3><p className="text-gray-600 text-sm">{selectedContactInfo.type}</p></div></div>) : ( <span className="text-gray-500">Select a contact...</span> )}<ChevronDown className={`w-5 h-5 text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} /></button>{isDropdownOpen && (<div className="absolute top-full mt-2 w-full bg-white/80 backdrop-blur-xl border border-black/10 rounded-xl shadow-lg z-20 animate-fade-in-down"><div className="p-2 border-b border-black/10"><input type="text" placeholder="Search contacts..." className="w-full bg-white/50 text-gray-900 placeholder-gray-600 rounded-lg px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-gray-700 outline-none" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div><ul className="max-h-60 overflow-y-auto">{filteredContacts.length > 0 ? filteredContacts.map(contact => (<li key={contact.id} onClick={() => { setCurrentSend(prev => ({ ...prev, selectedContact: contact.id })); setIsDropdownOpen(false); setSearchTerm(''); }} className="p-3 hover:bg-black/10 cursor-pointer flex items-center"><div className="bg-gray-700 rounded-full p-2 mr-3"><User className="w-4 h-4 text-white" /></div><div><h3 className="text-gray-800 font-semibold">{contact.name}</h3><p className="text-gray-600 text-sm">{contact.type}</p></div></li>)) : <li className="p-3 text-center text-gray-500">No contacts found.</li>}</ul></div>)}</div></div><div><label className="block text-gray-800 text-lg font-medium mb-4">3. Add Files</label><div onClick={() => fileInputRef.current.click()} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${dragOver ? 'border-gray-800 bg-black/10' : 'border-black/30 bg-black/5'}`}><input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple accept="audio/*,audio/mpeg,audio/wav,audio/x-m4a" className="hidden" /><Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" /><p className="text-gray-800 text-lg mb-2">Drag & drop files here, or click to select</p><p className="text-gray-500">Only metadata (like title) is saved.</p></div><div className="mt-4"><button onClick={connectDropbox} className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl font-semibold flex items-center justify-center transition-colors shadow-lg"><Plus className="w-5 h-5 mr-2" />Select Files from Dropbox</button></div></div>{selectedBeatsData.length > 0 && (<div><label className="block text-gray-800 text-lg font-medium mb-4">Beats in this Pack ({selectedBeatsData.length})</label><div className="space-y-3">{selectedBeatsData.map(beat => (<div key={beat.id} className="bg-black/5 backdrop-blur-lg rounded-lg p-3 flex items-center justify-between border border-black/10"><div className="flex items-center flex-1"><button onClick={() => playAudio(beat.id, beat.url)} className="bg-gray-700 hover:bg-gray-800 rounded-full p-2 mr-4 transition-colors"><Play className="w-4 h-4 text-white" /></button><h4 className="text-gray-800 font-semibold">{beat.title}</h4></div><button onClick={() => removeBeatFromSend(beat.id)} className="text-red-500 hover:text-red-700 p-1"><X className="w-5 h-5" /></button></div>))}</div></div>)}{selectedContactInfo && selectedBeatsData.length > 0 && (<div className="flex justify-center mt-8"><button onClick={handleLogOrUpdatePack} className="bg-gray-800 hover:bg-gray-900 text-white px-8 py-3 rounded-xl font-semibold flex items-center text-lg transition-all transform hover:scale-105 shadow-lg"><BookMarked className="w-5 h-5 mr-3" />{currentSend.id ? `Update Pack for ${selectedContactInfo?.name}` : 'File Away Pack as Sent'}</button></div>)}</div>)}
        {activeTab === 'contacts' && (<div className="bg-black/5 backdrop-blur-2xl rounded-3xl border border-black/10 shadow-xl p-8"><div className="flex items-center justify-between mb-8"><div className="flex items-center"><div className="bg-gray-800 rounded-full p-3 mr-4"><Users className="w-6 h-6 text-white" /></div><h2 className="text-3xl font-bold text-gray-800">Contacts</h2></div><button onClick={openAddContactModal} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center shadow-lg"><Plus className="w-4 h-4 mr-2" />Add Contact</button></div><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{contacts.map(contact => (<div key={contact.id} className="bg-black/5 rounded-lg p-6 border border-black/10 group relative"><div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity"><button onClick={(e) => { e.stopPropagation(); openEditContactModal(contact);}} className="p-2 rounded-full bg-white/50 hover:bg-white/80"><Pencil className="w-4 h-4 text-blue-600"/></button><button onClick={(e) => { e.stopPropagation(); setContactToDelete(contact);}} className="p-2 rounded-full bg-white/50 hover:bg-white/80"><Trash2 className="w-4 h-4 text-red-600"/></button></div><div onClick={() => handleContactClick(contact.id)} className="cursor-pointer"><div className="flex items-center mb-4"><div className="bg-gray-700 rounded-full p-3 mr-4"><User className="w-6 h-6 text-white" /></div><div><h3 className="text-gray-800 font-bold text-lg">{contact.name}</h3><p className="text-gray-600">{contact.type}</p></div></div><p className="text-gray-700 mb-2 truncate">📧 {contact.email || 'No email'}</p><p className="text-gray-700 mb-4">📱 {contact.phone || 'No phone'}</p><div className="text-sm text-gray-500 border-t border-black/10 pt-2 mt-2">Sounds Sent: {sentPacks.filter(p => p.contactId === contact.id).reduce((acc, p) => acc + p.beatIds.length, 0)}</div></div></div>))}</div></div>)}
        {activeTab === 'sound-sent' && (<div className="bg-black/5 backdrop-blur-2xl rounded-3xl border border-black/10 shadow-xl p-8">{selectedPackForHistory ? (<div><button onClick={() => setSelectedPackForHistory(null)} className="flex items-center font-semibold text-gray-700 hover:text-black mb-6"><ChevronLeft className="w-5 h-5 mr-1" />Back to All Packs</button><h2 className="text-2xl font-bold mb-4">Sent to {historyContact ? historyContact.name : '...'} {selectedPackForHistory.packName && <span className="text-lg font-normal text-gray-500 ml-2">[{selectedPackForHistory.packName}]</span>}</h2><div className="space-y-3">{selectedPackForHistory.beatIds.map(beatId => { const beat = beats.find(b => b.id === beatId); return (<div key={beatId} className="bg-black/5 rounded-lg p-4 flex items-center border border-black/10"><Music className="w-5 h-5 mr-4 text-gray-600" /><span className="font-semibold">{beat ? beat.title : 'Unknown Beat'}</span></div>);})}</div></div>) : (<div><h2 className="text-3xl font-bold text-gray-800 mb-6">Packs I Sent</h2><div className="space-y-4">{sentPacks.length > 0 ? sentPacks.map(pack => { const contact = contacts.find(c => c.id === pack.contactId); return (<div key={pack.id} className="bg-black/5 rounded-lg p-4 border border-black/10 hover:bg-black/10 transition-all flex justify-between items-center group"><div><div className="font-semibold text-lg cursor-pointer" onClick={() => setSelectedPackForHistory(pack)}>{contact ? contact.name : 'Unknown Contact'} {pack.packName && <span className="text-gray-500 font-normal">[{pack.packName}]</span>}</div><div className="text-sm text-gray-600 cursor-pointer" onClick={() => setSelectedPackForHistory(pack)}>{new Date(pack.date).toLocaleDateString()}</div></div><div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity"><button onClick={() => handleEditPack(pack)} className="p-2 text-gray-600 hover:text-blue-600"><Pencil className="w-4 h-4" /></button><button onClick={() => setPackToDelete(pack.id)} className="p-2 text-gray-600 hover:text-red-600"><Trash2 className="w-4 h-4" /></button><div className="text-sm font-medium text-gray-700 bg-gray-300/50 px-3 py-1 rounded-full">{pack.beatIds.length} beat(s)</div></div></div>);}) : (<p className="text-center text-gray-500 py-12">You haven't logged any sent packs yet.</p>)}</div></div>)}</div>)}
{activeTab === 'library' && (
  <div className="bg-black/5 backdrop-blur-2xl rounded-3xl border border-black/10 shadow-xl p-8">
    <div className="flex items-center justify-between mb-8">
      <div className="flex items-center">
        <div className="bg-gray-800 rounded-full p-3 mr-4">
          <Music className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-800">📚 Library</h2>
      </div>
      {dropboxLibraryToken ? (
        <button onClick={disconnectLibrary} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-lg">
          <X className="w-4 h-4 mr-2" />Disconnect
        </button>
      ) : (
        <button onClick={connectDropboxLibrary} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-lg">
          <Plus className="w-4 h-4 mr-2" />Connect Library
        </button>
      )}
    </div>

    {!dropboxLibraryToken ? (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Connect your Dropbox to access your personal Soundfile library</p>
        <p className="text-gray-500 text-sm mb-6">This creates a dedicated /Apps/Soundfile/ folder in your Dropbox</p>
        <button onClick={connectDropboxLibrary} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-lg">
          Connect Dropbox Library
        </button>
      </div>
    ) : (
      <div>
        {/* Toolbar */}
        <div className="mb-6 flex items-center justify-between bg-white/50 p-4 rounded-xl">
          <div className="flex items-center space-x-4">
            {currentLibraryPath && (
              <button 
                onClick={() => {
                  const pathParts = currentLibraryPath.split('/').filter(Boolean);
                  pathParts.pop();
                  loadLibraryFolder(pathParts.length ? '/' + pathParts.join('/') : '');
                  setSelectedLibraryFiles([]);
                }} 
                className="text-gray-600 hover:text-black flex items-center font-medium"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />Back
              </button>
            )}
            <span className="text-gray-600 text-sm font-mono bg-gray-200 px-3 py-1 rounded-md">
              {currentLibraryPath || '/Apps/Soundfile/'}
            </span>
            {selectedLibraryFiles.length > 0 && (
              <span className="text-blue-600 font-semibold">
                {selectedLibraryFiles.length} selected
              </span>
            )}
          </div>
          <div className="flex space-x-2">
            {selectedLibraryFiles.length > 0 && (
              <>
                <button
                  onClick={async () => {
                    const audioFiles = libraryFiles.filter(f => 
                      selectedLibraryFiles.includes(f.id) && f['.tag'] === 'file'
                    );
                    for (const file of audioFiles) {
                      await addLibraryFileToPack(file);
                    }
                    setSelectedLibraryFiles([]);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-lg font-semibold"
                >
                  <Plus className="w-4 h-4 mr-2" />Add {selectedLibraryFiles.length} to Pack
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm(`Delete ${selectedLibraryFiles.length} item(s)?`)) {
                      for (const fileId of selectedLibraryFiles) {
                        const file = libraryFiles.find(f => f.id === fileId);
                        if (file) await deleteLibraryFile(file.path_display);
                      }
                      setSelectedLibraryFiles([]);
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-lg font-semibold"
                >
                  <Trash2 className="w-4 h-4 mr-2" />Delete
                </button>
              </>
            )}
            <input 
              type="file" 
              ref={libraryUploadRef}
              multiple 
              accept="audio/*"
              className="hidden"
              onChange={(e) => uploadToLibrary(Array.from(e.target.files))}
              disabled={uploadingFiles}
            />
            <button
              onClick={() => libraryUploadRef.current?.click()}
              disabled={uploadingFiles}
              className={`${uploadingFiles ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-lg font-semibold`}
            >
              {uploadingFiles ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploadingFiles ? 'Uploading...' : 'Upload'}
            </button>
            <button
              onClick={() => {
                const folderName = prompt('Enter folder name:');
                if (folderName) createLibraryFolder(folderName);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-lg font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />Folder
            </button>
          </div>
        </div>

        {/* File List */}
        {loadingLibrary ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-gray-600" />
          </div>
        ) : (
          <div className="space-y-2">
            {libraryFiles.length > 0 ? libraryFiles.map(file => {
              const isSelected = selectedLibraryFiles.includes(file.id);
              return (
                <div 
                  key={file.id} 
                  className={`${isSelected ? 'bg-blue-100 border-blue-500' : 'bg-white/60 border-gray-300'} rounded-lg p-4 flex items-center justify-between border hover:shadow-md transition-all group`}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLibraryFiles(prev => [...prev, file.id]);
                      } else {
                        setSelectedLibraryFiles(prev => prev.filter(id => id !== file.id));
                      }
                    }}
                    className="mr-4 w-5 h-5 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />

                  <div className="flex items-center flex-1 min-w-0">
                    {file['.tag'] === 'folder' ? (
                      <>
                        <div className="bg-yellow-500 rounded-lg p-3 mr-4 flex-shrink-0">
                          <Music className="w-5 h-5 text-white" />
                        </div>
                        <button
                          onClick={() => {
                            loadLibraryFolder(file.path_display);
                            setSelectedLibraryFiles([]);
                          }}
                          className="text-left truncate"
                        >
                          <h4 className="text-gray-800 font-semibold">📁 {file.name}</h4>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={async () => {
                            const response = await fetch('https://api.dropboxapi.com/2/files/get_temporary_link', {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${dropboxLibraryToken}`,
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({ path: file.path_display })
                            });
                            const data = await response.json();
                            playAudio(file.id, data.link);
                          }}
                          className="bg-purple-500 hover:bg-purple-600 rounded-lg p-3 mr-4 flex-shrink-0 transition-colors"
                        >
                          {currentlyPlaying === file.id ? (
                            <Pause className="w-5 h-5 text-white" />
                          ) : (
                            <Play className="w-5 h-5 text-white" />
                          )}
                        </button>
                        <h4 className="text-gray-800 font-semibold truncate flex-1">🎵 {file.name}</h4>
                      </>
                    )}
                  </div>
                  
                  {file['.tag'] === 'file' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addLibraryFileToPack(file);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm opacity-0 group-hover:opacity-100"
                    >
                      Add to Pack
                    </button>
                  )}
                </div>
              );
            }) : (
              <div className="text-center py-12 bg-white/40 rounded-xl border-2 border-dashed border-gray-400">
                <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-semibold mb-2">Your library is empty</p>
                <p className="text-gray-500 text-sm">Upload some files to get started!</p>
              </div>
            )}
          </div>
        )}

        {/* Storage Info */}
        <div className="mt-6 p-4 bg-white/50 rounded-xl border border-gray-300">
          <p className="text-sm text-gray-600">
            💾 <span className="font-semibold">Storage:</span> Based on your Dropbox plan
          </p>
          <p className="text-xs text-gray-500 mt-1">Files are stored in /Apps/Soundfile/ in your Dropbox</p>
        </div>
      </div>
    )}
  </div>
)}

        {activeTab === 'about' && (<div className="bg-black/5 backdrop-blur-2xl rounded-3xl border border-black/10 shadow-xl p-8"><div className="flex items-center mb-6"><div className="bg-gray-800 rounded-full p-3 mr-4"><Info className="w-6 h-6 text-white" /></div><h2 className="text-3xl font-bold text-gray-800">About SoundFile</h2></div><div className="prose prose-lg text-gray-700 max-w-none"><p>Soundfile is meant to help creatives who are constantly sending out audio files to a bunch of people keep track of which files were sent to who and when.</p><p>Rather than having to write long lists in notes apps, or track messages and emails, simply drag and drop the files you just sent out to someone, select the name of the person you just sent it to, and done! The log of the pack you sent is saved and you can use it to keep track of your send outs in the future.</p></div></div>)}
      </main>
      
      {showAllBeats && <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"><div className="bg-gray-200/90 backdrop-blur-2xl rounded-2xl border border-black/10 p-8 w-full max-w-4xl max-h-[80vh] flex flex-col"><div className="flex items-center justify-between mb-6 flex-shrink-0"><h3 className="text-2xl font-bold text-gray-800">All Beats ({beats.length})</h3><button onClick={() => setShowAllBeats(false)} className="text-gray-600 hover:text-black"><X /></button></div><div className="space-y-3 overflow-y-auto pr-2">{beats.length > 0 ? beats.map(beat => { const recipients = sentPacks.filter(p => p.beatIds.includes(beat.id)).map(p => contacts.find(c => c.id === p.contactId)?.name).filter(Boolean); const uniqueRecipients = [...new Set(recipients)]; return (<div key={beat.id} className="bg-black/5 rounded-lg p-4 flex items-center justify-between border border-black/10"><div className="flex items-center"><button onClick={() => playAudio(beat.id, localBeatUrls.get(beat.id))} className="bg-gray-700 hover:bg-gray-800 rounded-full p-2 mr-4 flex-shrink-0"><Play className="w-4 h-4 text-white" /></button><div><h4 className="text-gray-800 font-semibold">{beat.title}</h4>{uniqueRecipients.length > 0 && <div className="text-xs text-gray-600 mt-1 flex items-center"><Users className="w-3 h-3 mr-1.5"/> Sent to: {uniqueRecipients.join(', ')}</div>}</div></div><div className="text-sm font-medium text-gray-700 bg-gray-300/50 px-2 py-1 rounded-md">{uniqueRecipients.length}</div></div>);}) : (<div className="text-center py-8 text-gray-500">No beats logged yet.</div>)}</div></div></div>}
      <ContactFormModal isOpen={showContactForm} onClose={() => setShowContactForm(false)} setNotification={setNotification} user={user} contactToEdit={contactToEdit} />
      <audio ref={audioRef} onEnded={() => setCurrentlyPlaying(null)} />
    </div>
  );
};

// --- App Component (Top Level) ---
const App = () => {
    const [user, setUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: '' });

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user);
            setIsAuthReady(true);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (notification.show) {
            const timer = setTimeout(() => {
                setNotification({ show: false, message: '' });
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [notification.show]);

    if (!isAuthReady) return <LoadingSpinner theme="light" />;

    return (
        <>
            <Notification message={notification.message} show={notification.show} isError={notification.isError} />
            {user === undefined ?
                <LoadingSpinner theme="light" /> :
                <MusicCRM user={user} setNotification={setNotification} />
            }
        </>
    );
};

export default App;
